import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, Input, Select, Spin, Tag, Table, Drawer, Descriptions, Empty } from '@arco-design/web-react'
import { Link } from 'react-router-dom'
import { getPricingApi, type PricingEntry } from '@/api/pricing'
import { renderModelTag, renderGroupTag } from '@/utils/render'
import { useStatusStore } from '@/stores/statusStore'
import { useBreakpoint } from '@/hooks/useBreakpoint'

export default function Pricing() {
  const status = useStatusStore((s) => s.status)
  const { isMobile } = useBreakpoint()
  const { data, isLoading } = useQuery({ queryKey: ['pricing'], queryFn: getPricingApi })
  const [keyword, setKeyword] = useState('')
  const [group, setGroup] = useState<string>('')
  const [endpoint, setEndpoint] = useState<string>('')
  const [detail, setDetail] = useState<PricingEntry | null>(null)

  const filtered = useMemo(() => {
    const list = data?.data || []
    return list.filter((x) => {
      if (keyword && !x.model_name?.toLowerCase().includes(keyword.toLowerCase())) return false
      if (group && !(x.enable_groups || []).includes(group)) return false
      if (endpoint && !(x.supported_endpoint_types || []).includes(endpoint)) return false
      return true
    })
  }, [data, keyword, group, endpoint])

  const columns = [
    { title: '模型', dataIndex: 'model_name', render: (v: string) => renderModelTag(v) },
    { title: '供应商', dataIndex: 'vendor_name', render: (v?: string) => v || '-' },
    { title: '倍率', dataIndex: 'model_ratio', render: (v?: number, row?: PricingEntry) => row?.model_price ? <Tag color="orange">${row.model_price} / 次</Tag> : <span>×{v ?? 1}</span> },
    { title: '补全倍率', dataIndex: 'completion_ratio', render: (v?: number) => `×${v ?? 1}` },
    {
      title: '可用分组',
      dataIndex: 'enable_groups',
      render: (v?: string[]) => (v?.length ? <div className="flex flex-wrap">{v.map((g) => renderGroupTag(g))}</div> : '-'),
    },
    {
      title: '端点',
      dataIndex: 'supported_endpoint_types',
      render: (v?: string[]) => (v?.length ? <div className="flex flex-wrap">{v.map((e) => <Tag key={e} size="small" className="mr-1">{e}</Tag>)}</div> : '-'),
    },
  ]

  if (status && !status.pricing) {
    return (
      <div className="min-h-screen bg-[var(--console-page-bg)] py-10 px-4 md:px-6">
        <Empty description="该系统未开放模型广场" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[var(--console-page-bg)] py-10 px-4 md:px-6">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#111827]">模型广场</h1>
            <p className="text-sm text-[#6B7280] mt-1">查看所有可用模型、分组与价格</p>
          </div>
          <Link to="/?auth=login" className="text-sm text-[var(--brand-primary)] hover:underline shrink-0">登录使用 →</Link>
        </div>

        <Card className="!rounded-2xl">
          <div className="flex flex-wrap gap-3 mb-4">
            <Input.Search
              placeholder="搜索模型名"
              value={keyword}
              onChange={setKeyword}
              className="w-full sm:w-[260px]"
              allowClear
            />
            <Select
              placeholder="分组"
              value={group}
              onChange={setGroup}
              allowClear
              className="w-full sm:w-[180px]"
              options={Object.keys(data?.usable_group || {}).map((g) => ({ label: g, value: g }))}
            />
            <Select
              placeholder="端点类型"
              value={endpoint}
              onChange={setEndpoint}
              allowClear
              className="w-full sm:w-[200px]"
              options={(data?.supported_endpoints || []).map((e) => ({ label: e, value: e }))}
            />
          </div>

          {isLoading ? (
            <div className="py-20 flex justify-center"><Spin /></div>
          ) : (
            <Table
              rowKey="model_name"
              columns={columns}
              data={filtered}
              scroll={{ x: 900 }}
              pagination={{ pageSize: 20, showTotal: true }}
              onRow={(row) => ({
                onClick: () => setDetail(row),
                style: { cursor: 'pointer' },
              })}
            />
          )}
        </Card>

        <Drawer
          visible={!!detail}
          onCancel={() => setDetail(null)}
          title="模型详情"
          width={isMobile ? '100%' : 480}
          footer={null}
        >
          {detail && (
            <Descriptions
              column={1}
              data={[
                { label: '模型名称', value: detail.model_name },
                { label: '供应商', value: detail.vendor_name || '-' },
                { label: '说明', value: detail.description || '-' },
                { label: '倍率', value: detail.model_price ? `固定 $${detail.model_price}` : `×${detail.model_ratio ?? 1}` },
                { label: '补全倍率', value: `×${detail.completion_ratio ?? 1}` },
                { label: '可用分组', value: (detail.enable_groups || []).join(', ') || '-' },
                { label: '端点类型', value: (detail.supported_endpoint_types || []).join(', ') || '-' },
              ]}
            />
          )}
        </Drawer>
      </div>
    </div>
  )
}
